import { supabase } from '../lib/supabase/client';
import { Booking } from '../types/models';
import { Database } from '../types/database.types';
import { emailService } from './emailService';

type BookingRow = Database['public']['Tables']['bookings']['Row'];
type BookingInsert = Database['public']['Tables']['bookings']['Insert'];

// Convert snake_case database row to camelCase application model
function convertToBooking(row: BookingRow): Booking {
  return {
    id: row.id,
    guestId: row.guest_id,
    roomId: row.room_id,
    checkInDate: row.check_in_date,
    checkOutDate: row.check_out_date,
    numberOfGuests: row.number_of_guests,
    totalPrice: row.total_price,
    bookingStatus: row.booking_status,
    paymentStatus: row.payment_status,
    guestName: row.guest_name,
    guestEmail: row.guest_email,
    guestPhone: row.guest_phone,
    specialRequests: row.special_requests,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export const bookingService = {
  async create(
    booking: Omit<Booking, 'id' | 'createdAt' | 'updatedAt'>,
    roomName?: string
  ): Promise<{
    data: Booking | null;
    error: Error | null;
  }> {
    try {
      // Convert camelCase to snake_case for database
      const insertData: BookingInsert = {
        guest_id: booking.guestId,
        room_id: booking.roomId,
        check_in_date: booking.checkInDate,
        check_out_date: booking.checkOutDate,
        number_of_guests: booking.numberOfGuests,
        total_price: booking.totalPrice,
        booking_status: booking.bookingStatus,
        payment_status: booking.paymentStatus,
        guest_name: booking.guestName,
        guest_email: booking.guestEmail,
        guest_phone: booking.guestPhone,
        special_requests: booking.specialRequests,
      };

      const { data, error } = await supabase
        .from('bookings')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;

      const bookingData = data ? convertToBooking(data) : null;

      // Send confirmation emails after successful booking creation
      if (bookingData) {
        // Se roomName non è fornito, recuperalo dal database
        let finalRoomName = roomName;
        
        if (!finalRoomName && booking.roomId) {
          const { data: roomData } = await supabase
            .from('rooms')
            .select('name')
            .eq('id', booking.roomId)
            .single();
          
          finalRoomName = roomData?.name || 'Camera';
        }

        // Invia email solo se abbiamo un nome camera
        if (finalRoomName) {
          const emailResult = await emailService.sendBookingConfirmation(
            bookingData,
            finalRoomName
          );
          
          if (!emailResult.success) {
            console.warn('Booking created but email sending failed:', emailResult.error);
            // Don't fail the booking creation if email fails
          }
        }
      }

      return { data: bookingData, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  async getByUserId(userId: string): Promise<{
    data: Booking[] | null;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase
        .from('bookings')
        .select('*')
        .eq('guest_id', userId)
        .order('check_in_date', { ascending: false });

      if (error) throw error;

      const bookings = data?.map(convertToBooking) || [];
      return { data: bookings, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  async getById(bookingId: string): Promise<{
    data: Booking | null;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase
        .from('bookings')
        .select('*')
        .eq('id', bookingId)
        .single();

      if (error) throw error;

      const booking = data ? convertToBooking(data) : null;
      return { data: booking, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  async updateStatus(
    bookingId: string,
    status: 'pending' | 'confirmed' | 'cancelled' | 'completed'
  ): Promise<{ data: Booking | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('bookings')
        .update({ booking_status: status })
        .eq('id', bookingId)
        .select()
        .single();

      if (error) throw error;

      const booking = data ? convertToBooking(data) : null;
      return { data: booking, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },
};